import {ReactElement} from 'react';
import {RootRoute, WebRoute} from './RootRoute';

import {Navigate, Outlet} from 'react-router-dom';
import {useAuth} from '../store/AuthProvider';
import {LoadingAnimation} from '../components/loading-animation/LoadingAnimation';
import {useAppSelector} from '../hooks/store/useAppSelector';
import {UserSelector} from '../store/user/UserSelector';
import {getFallbackRoute} from '../utils/auth/validateReturnUrlPermissions';
import {CompanySelector} from '../store/company/CompanySelector';
import {EnvironmentDownScreen} from '../components/environment-down-screen/EnvironmentDownScreen';

interface ProtectedRouteProps {
  redirectPath?: WebRoute;
  children?: ReactElement;
}

const NavigateToLogin = () => {
  return (
    <Navigate
      to={`${RootRoute.LogIn}?returnUrl=${encodeURIComponent(
        window.location.pathname + window.location.search,
      )}`}
      replace
    />
  );
};

export const ProtectedAdminRoute = ({
  redirectPath = RootRoute.LogIn,
  children,
}: ProtectedRouteProps) => {
  const {user, isLoading, isAdmin, isAdminView} = useAuth();

  const userDetails = useAppSelector(UserSelector.getUserDetails);
  const companyDetails = useAppSelector(CompanySelector.getCompanyDetails);

  if (isLoading) {
    return <LoadingAnimation overwriteIsBusy />;
  }

  if (!user) {
    return <NavigateToLogin />;
  }

  // Wait for admin check to complete (isAdmin can be null while checking)
  if (isAdmin === null) {
    return <LoadingAnimation overwriteIsBusy />;
  }

  // If user is authenticated but not an admin, redirect to their appropriate dashboard
  // instead of forcing them to log in again
  if (!isAdmin) {
    return <Navigate to={getFallbackRoute(false, isAdminView)} replace />;
  }

  if (!userDetails && user) {
    return <LoadingAnimation overwriteIsBusy />;
  }

  const environmentDown = companyDetails?.settings?.isEnvironmentDown;
  if (environmentDown) {
    return (
      <EnvironmentDownScreen
        title={environmentDown.title}
        description={environmentDown.description}
      />
    );
  }

  return children ? children : <Outlet />;
};
